import { z } from 'zod'

import { GeoSpatialFileType } from './locutus'

/** The kinds of data a user takes out of Panorama as a file. */
export enum ExportSurface {
  Prescriptions = 'prescriptions',
  Boundaries = 'boundaries',
  FieldList = 'field-list',
  AsAppliedData = 'as-applied-data'
}

export const exportSurfaceSchema = z.enum(ExportSurface)

type ExportFormat = {
  /** What a grower calls the format. */
  name: string
  fileType: GeoSpatialFileType
}

/** Describes what a user gets out of Panorama — never where to get it or who may. */
type ExportSurfaceGuide = {
  /** What the export produces, as a noun phrase, for picking between surfaces. */
  subject: string
  formats: readonly [ExportFormat, ...ExportFormat[]]
}

const SHAPEFILE: ExportFormat = { name: 'Shapefile', fileType: GeoSpatialFileType.ShapeFile }
const GEN3_2020: ExportFormat = { name: '20|20 file', fileType: GeoSpatialFileType.Gen3_2020 }

/**
 * What Panorama hands back for each export surface, in the terms a user would
 * recognize. A boundary or prescription downloads from the row menu on the
 * Boundaries or Prescriptions planning screen; a CFF downloads from the Fields
 * planning screen; as-applied data downloads in bulk, as 2020 files, from the
 * Edit screen under Manage Operation > Data.
 */
export const EXPORT_SURFACES: Readonly<Record<ExportSurface, ExportSurfaceGuide>> = {
  [ExportSurface.Prescriptions]: {
    subject: 'variable-rate prescriptions',
    formats: [GEN3_2020, SHAPEFILE]
  },
  [ExportSurface.Boundaries]: {
    subject: 'field boundaries',
    formats: [GEN3_2020, SHAPEFILE]
  },
  [ExportSurface.FieldList]: {
    subject: 'the client, farm, and field list (a CFF)',
    formats: [GEN3_2020]
  },
  [ExportSurface.AsAppliedData]: {
    subject: 'as-applied data from passes already run',
    // A single pass's layer also downloads as a Shapefile from the pass details
    // pane. We omit it because the bulk download produces only 20|20 files, and
    // this list cannot say which path gives which format.
    formats: [GEN3_2020]
  }
}
